import { useCallback, useEffect, useRef } from "react";
import { replayPendingItem } from "./performanceTrackerApi";
import { createTrackerChannel } from "./trackerBroadcast";
import {
  enqueuePending,
  isRetryableTrackerError,
  takePending,
} from "./trackerPendingQueue";
import {
  activityMsFromState,
  shouldPostActiveFromActivity,
  shouldPostIdle,
} from "./trackerRuntimeRules";
import { useActivityDetection } from "./useActivityDetection";
import { useVisibilityDetection } from "./useVisibilityDetection";

function isVisible() {
  return typeof document === "undefined" || document.visibilityState === "visible";
}

function newId() {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  return `${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

export function useTrackerRuntime({
  state,
  applyState,
  refreshCurrent,
  instance,
  accountsRef,
}) {
  const stateRef = useRef(state);
  stateRef.current = state;
  const lastActivityAtRef = useRef(Date.now());
  const lastActivityTimeRef = useRef(state.lastActivityTime);
  lastActivityTimeRef.current = state.lastActivityTime;
  const lastActivePostAtRef = useRef(0);
  const activityPostInFlightRef = useRef(false);
  const idlePostInFlightRef = useRef(false);
  const skipBroadcastRef = useRef(false);
  const flushingRef = useRef(false);
  const channelRef = useRef({ post() {}, close() {} });

  const publishState = useCallback((data) => {
    if (!data || skipBroadcastRef.current) return;
    channelRef.current.post({ type: "state", data });
  }, []);

  const postSilentActivity = useCallback(
    async ({ signal, sessionId }) => {
      const current = stateRef.current;
      const id = sessionId || current.sessionId;
      if (!id || current.enabled === false) return null;

      const payload = {
        sessionId: id,
        signal,
        clientTimestamp: new Date().toISOString(),
        idempotencyKey: newId(),
      };

      try {
        const data = await replayPendingItem(instance, accountsRef.current, {
          kind: "activity",
          ...payload,
        });
        applyState(data);
        publishState(data);
        return data;
      } catch (err) {
        if (err?.status === 409) {
          refreshCurrent().catch(() => {});
          return null;
        }
        if (isRetryableTrackerError(err)) {
          enqueuePending({ kind: "activity", ...payload });
        }
        throw err;
      }
    },
    [accountsRef, applyState, instance, publishState, refreshCurrent],
  );

  const flushPending = useCallback(async () => {
    if (flushingRef.current) return;
    const items = takePending();
    if (!items.length) return;
    flushingRef.current = true;
    try {
      for (const item of items) {
        try {
          const data = await replayPendingItem(instance, accountsRef.current, item);
          if (data) {
            applyState(data);
            publishState(data);
          }
        } catch (err) {
          if (err?.status === 409) {
            try {
              await refreshCurrent();
            } catch {
              /* continue remaining items */
            }
            continue;
          }
          if (isRetryableTrackerError(err)) {
            enqueuePending(item);
            break;
          }
        }
      }
    } finally {
      flushingRef.current = false;
    }
  }, [accountsRef, applyState, instance, publishState, refreshCurrent]);

  useEffect(() => {
    const channel = createTrackerChannel((message) => {
      if (message?.type === "activity" && message.at) {
        lastActivityAtRef.current = message.at;
        return;
      }
      if (message?.type === "state" && message.data) {
        skipBroadcastRef.current = true;
        applyState(message.data);
        skipBroadcastRef.current = false;
      }
    });
    channelRef.current = channel;

    const onOnline = () => {
      flushPending()
        .then(() => refreshCurrent())
        .catch(() => {});
    };
    window.addEventListener("online", onOnline);

    return () => {
      channel.close();
      channelRef.current = { post() {}, close() {} };
      window.removeEventListener("online", onOnline);
    };
  }, [applyState, flushPending, refreshCurrent]);

  useEffect(() => {
    if (state.status === "RUNNING" || state.status === "AUTO_PAUSED") {
      lastActivityAtRef.current = activityMsFromState({
        lastActivityTime: lastActivityTimeRef.current,
      });
    }
  }, [state.sessionId, state.status]);

  const onGenuineActivity = useCallback(() => {
    const now = Date.now();
    lastActivityAtRef.current = now;
    channelRef.current.post({ type: "activity", at: now });

    const current = stateRef.current;
    if (
      !shouldPostActiveFromActivity({
        status: current.status,
        visible: isVisible(),
        autoResumeEnabled: current.autoResumeEnabled,
      })
    ) {
      return;
    }

    const heartbeatMs = Math.max(
      1000,
      (Number(current.activityHeartbeatSeconds) || 60) * 1000,
    );
    if (current.status === "RUNNING" && now - lastActivePostAtRef.current < heartbeatMs) {
      return;
    }
    if (activityPostInFlightRef.current) return;

    lastActivePostAtRef.current = now;
    activityPostInFlightRef.current = true;
    postSilentActivity({ signal: "ACTIVE" })
      .catch(() => {})
      .finally(() => {
        activityPostInFlightRef.current = false;
      });
  }, [postSilentActivity]);

  useActivityDetection(onGenuineActivity, {
    enabled:
      state.enabled !== false &&
      (state.status === "RUNNING" || state.status === "AUTO_PAUSED"),
    debounceMs: state.status === "AUTO_PAUSED" ? 0 : 1500,
  });

  useVisibilityDetection({
    onVisible: () => {
      refreshCurrent().catch(() => {});
      flushPending().catch(() => {});
    },
  });

  useEffect(() => {
    const live = state.status === "RUNNING";
    if (!live || state.enabled === false) return undefined;

    const idleId = window.setInterval(() => {
      const current = stateRef.current;
      if (!isVisible() || current.status !== "RUNNING" || idlePostInFlightRef.current) return;
      if (
        !shouldPostIdle({
          status: current.status,
          visible: true,
          lastActivityAt: lastActivityAtRef.current,
          thresholdSeconds: current.inactivityThresholdSeconds,
        })
      ) {
        return;
      }
      idlePostInFlightRef.current = true;
      postSilentActivity({ signal: "IDLE" })
        .catch(() => {})
        .finally(() => {
          idlePostInFlightRef.current = false;
        });
    }, 1000);

    const heartbeatMs = Math.max(
      1000,
      (Number(state.activityHeartbeatSeconds) || 60) * 1000,
    );
    const heartbeatId = window.setInterval(() => {
      const current = stateRef.current;
      if (!isVisible() || current.status !== "RUNNING") return;
      const sinceActivity = Date.now() - lastActivityAtRef.current;
      if (sinceActivity < heartbeatMs) {
        postSilentActivity({ signal: "ACTIVE" }).catch(() => {});
      }
    }, heartbeatMs);

    return () => {
      window.clearInterval(idleId);
      window.clearInterval(heartbeatId);
    };
  }, [
    postSilentActivity,
    state.activityHeartbeatSeconds,
    state.enabled,
    state.sessionId,
    state.status,
  ]);

  return { publishState };
}
